import { type Meta } from '../types/docs';
import { type UploadAnalyticsCommonParams } from './uploadAnalyticsTypes';

type UploadAnalyticsCommonMetaEntry =
  | {
      name: keyof UploadAnalyticsCommonParams;
      description: string;
      required: boolean;
      type: 'string' | 'number' | 'boolean';
    }
  | { name: keyof UploadAnalyticsCommonParams; description: string; required: boolean; values: string[]; type: 'enum' }
  | {
      name: keyof UploadAnalyticsCommonParams;
      description: string;
      required: boolean;
      values: string[];
      type: 'array';
    };

// Using a private const with `satisfies` so that:
//  1. each `name` is validated against keyof UploadAnalyticsCommonParams (no typos, renames caught)
//  2. literal name types are preserved for the exhaustiveness check below
const _uploadAnalyticsCommonMetaParams = [
  {
    description: 'Client-side upload id',
    name: 'upload_id',
    required: true,
    type: 'string',
  },
  {
    description: 'Upload batch id',
    name: 'batch_id',
    required: true,
    type: 'string',
  },
  {
    description: 'Created clip id when available',
    name: 'clip_id',
    required: false,
    type: 'string',
  },
  {
    description: 'Workspace id for private uploads',
    name: 'workspace_id',
    required: false,
    type: 'string',
  },
  {
    description: 'Asset id for version uploads when available',
    name: 'asset_id',
    required: false,
    type: 'string',
  },
  {
    description: 'Destination board id when available',
    name: 'board_id',
    required: false,
    type: 'string',
  },
  {
    description: 'Short board id for content collection uploads',
    name: 'short_board_id',
    required: false,
    type: 'string',
  },
  {
    description: 'Anonymous content collection session id when available',
    name: 'session_id',
    required: false,
    type: 'string',
  },
  {
    description: 'Upload flow source',
    name: 'source',
    required: true,
    type: 'enum',
    values: ['private_upload', 'content_collection'],
  },
  {
    description: 'Code path emitting the event',
    name: 'context',
    required: true,
    type: 'enum',
    values: [
      'private_upload_manager',
      'workspace_uploads',
      'content_collection_upload_manager',
      'private_large_part_upload',
      'content_collection_large_part_upload',
    ],
  },
  {
    description: 'Upload destination type',
    name: 'upload_target',
    required: true,
    type: 'enum',
    values: ['workspace', 'board', 'asset_version', 'content_collection'],
  },
  {
    description: 'Whether the upload creates a new asset version',
    name: 'is_version',
    required: false,
    type: 'boolean',
  },
  {
    description: 'Whether the upload uses multipart transfer',
    name: 'is_large_upload',
    required: true,
    type: 'boolean',
  },
  {
    description: 'Total file size in bytes',
    name: 'file_size_bytes',
    required: true,
    type: 'number',
  },
  {
    description: 'File extension without the dot',
    name: 'file_extension',
    required: false,
    type: 'string',
  },
  {
    description: 'File MIME type',
    name: 'file_mime_type',
    required: false,
    type: 'string',
  },
  {
    description: 'Air clip type inferred for the file',
    name: 'file_type',
    required: false,
    type: 'string',
  },
  {
    description: 'Number of path segments in folder upload paths',
    name: 'file_path_depth',
    required: false,
    type: 'number',
  },
  {
    description: 'Whether the file came from a nested folder path',
    name: 'has_folder_path',
    required: false,
    type: 'boolean',
  },
  {
    description: 'Image width when known',
    name: 'image_width',
    required: false,
    type: 'number',
  },
  {
    description: 'Image height when known',
    name: 'image_height',
    required: false,
    type: 'number',
  },
  {
    description: 'Expected upload part count',
    name: 'part_count',
    required: true,
    type: 'number',
  },
  {
    description: 'Configured multipart chunk size in bytes',
    name: 'chunk_size_bytes',
    required: false,
    type: 'number',
  },
  {
    description: 'Observed browser/network connectivity state',
    name: 'connectivity_state',
    required: true,
    type: 'enum',
    values: ['online', 'offline', 'unknown'],
  },
  {
    description: 'Current global upload speed estimate in bytes per second',
    name: 'upload_speed_bytes_per_second',
    required: false,
    type: 'number',
  },
  {
    description: 'Upload status in redux at event time',
    name: 'upload_status',
    required: false,
    type: 'string',
  },
  {
    description: 'Bytes uploaded according to current progress state',
    name: 'bytes_uploaded',
    required: false,
    type: 'number',
  },
  {
    description: 'Number of completed multipart parts',
    name: 'uploaded_part_count',
    required: false,
    type: 'number',
  },
] satisfies UploadAnalyticsCommonMetaEntry[];

// Compile-time check: every key in UploadAnalyticsCommonParams must have a corresponding meta entry.
// If you add a field to UploadAnalyticsCommonParams without adding a matching entry here, this line errors.
type _MetaParamNames = (typeof _uploadAnalyticsCommonMetaParams)[number]['name'];
type _MissingMetaParams = Exclude<keyof Required<UploadAnalyticsCommonParams>, _MetaParamNames>;
const _assertNoMissingMetaParams: [_MissingMetaParams] extends [never] ? true : never = true;
void _assertNoMissingMetaParams;

export const UPLOAD_ANALYTICS_COMMON_META_PARAMS: NonNullable<Meta['params']> = _uploadAnalyticsCommonMetaParams;
